import React, { useState } from 'react';
import { Button } from '../UI/button';
import { Card, CardContent, CardHeader, CardTitle } from '../UI/card';
import { Label } from '../UI/label';
import { Loader2, Calendar, Building, FileText, Mail } from 'lucide-react';

/**
 * COMPOSANT: GÉNÉRATEUR D'APPEL À COTISATION
 * 
 * Formulaire de génération d'appel à cotisation
 */

const CallGenerator = ({ enterprises, onGenerate, generating = false, existingCalls = [] }) => {
  const [formData, setFormData] = useState({
    enterpriseId: '',
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear()
  });

  const [errors, setErrors] = useState({});
  
  // Vérifier si un appel existe déjà pour cette période
  const callExists = existingCalls.find(call => 
    call.enterpriseId === formData.enterpriseId &&
    call.month === formData.month &&
    call.year === formData.year
  );

  const months = [
    { value: 1, label: 'Janvier' },
    { value: 2, label: 'Février' },
    { value: 3, label: 'Mars' },
    { value: 4, label: 'Avril' },
    { value: 5, label: 'Mai' },
    { value: 6, label: 'Juin' },
    { value: 7, label: 'Juillet' },
    { value: 8, label: 'Août' },
    { value: 9, label: 'Septembre' },
    { value: 10, label: 'Octobre' },
    { value: 11, label: 'Novembre' },
    { value: 12, label: 'Décembre' }
  ];

  const currentYear = new Date().getFullYear();
  const years = [currentYear - 1, currentYear, currentYear + 1];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.enterpriseId) {
      newErrors.enterpriseId = 'Entreprise obligatoire';
    }

    if (!formData.month) {
      newErrors.month = 'Mois obligatoire';
    }

    if (!formData.year) {
      newErrors.year = 'Année obligatoire';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      onGenerate(formData);
    }
  };

  const selectedEnterprise = enterprises?.find(e => e.value === formData.enterpriseId);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <FileText className="w-5 h-5 mr-2" />
          Générer un appel à cotisation
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Sélection entreprise */}
          <div>
            <Label htmlFor="enterpriseId">
              Entreprise <span className="text-red-500">*</span>
            </Label>
            <select
              id="enterpriseId"
              value={formData.enterpriseId}
              onChange={(e) => handleChange('enterpriseId', e.target.value)}
              disabled={generating}
              className={`w-full mt-1 px-3 py-2 border rounded-md ${
                errors.enterpriseId ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">-- Sélectionnez une entreprise --</option>
              {enterprises?.map(enterprise => (
                <option key={enterprise.value} value={enterprise.value}>
                  {enterprise.label}
                </option>
              ))}
            </select>
            {errors.enterpriseId && (
              <p className="text-sm text-red-500 mt-1">{errors.enterpriseId}</p>
            )}
          </div>

          {/* Période */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Mois */}
            <div>
              <Label htmlFor="month">
                Mois <span className="text-red-500">*</span>
              </Label>
              <select
                id="month"
                value={formData.month}
                onChange={(e) => handleChange('month', parseInt(e.target.value))}
                disabled={generating}
                className={`w-full mt-1 px-3 py-2 border rounded-md ${
                  errors.month ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                {months.map(month => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </select>
              {errors.month && (
                <p className="text-sm text-red-500 mt-1">{errors.month}</p>
              )}
            </div>

            {/* Année */}
            <div>
              <Label htmlFor="year">
                Année <span className="text-red-500">*</span>
              </Label>
              <select
                id="year"
                value={formData.year}
                onChange={(e) => handleChange('year', parseInt(e.target.value))}
                disabled={generating}
                className={`w-full mt-1 px-3 py-2 border rounded-md ${
                  errors.year ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                {years.map(year => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
              {errors.year && (
                <p className="text-sm text-red-500 mt-1">{errors.year}</p>
              )}
            </div>
          </div>

          {/* Informations entreprise sélectionnée */}
          {selectedEnterprise && (
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-start space-x-3">
                  <Building className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-semibold text-blue-900 mb-2">
                      Informations de l'entreprise
                    </h4>
                    <div className="space-y-1 text-sm text-blue-800">
                      <p>
                        <span className="font-medium">Nom:</span> {selectedEnterprise.label}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Informations sur la génération */}
          <Card className="bg-gray-50 border-gray-200">
            <CardContent className="p-4">
              <h4 className="font-semibold text-sm mb-2">📋 Ce qui sera généré</h4>
              <ul className="text-xs text-muted-foreground space-y-1">
                <li className="flex items-center">
                  <Calendar className="w-3 h-3 mr-2" />
                  Fichier Excel avec la liste des cotisations en attente
                </li>
                <li className="flex items-center">
                  <FileText className="w-3 h-3 mr-2" />
                  Document PDF récapitulatif
                </li>
                <li className="flex items-center">
                  <Mail className="w-3 h-3 mr-2" />
                  Possibilité d'envoi par email après génération
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Message si appel existe déjà */}
          {callExists && (
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="p-4">
                <div className="flex items-center text-amber-800">
                  <FileText className="w-5 h-5 mr-2 text-amber-600" />
                  <div className="text-sm">
                    <p className="font-semibold">Un appel existe déjà pour cette période</p>
                    <p className="text-xs mt-1">
                      Statut : <span className="font-medium">{callExists.status}</span> • 
                      Créé le : {new Date(callExists.createdAt).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Bouton de génération */}
          <Button
            type="submit"
            disabled={generating || !formData.enterpriseId || callExists}
            className="w-full"
          >
            {generating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Génération en cours...
              </>
            ) : callExists ? (
              <>
                <FileText className="w-4 h-4 mr-2" />
                Appel déjà généré pour cette période
              </>
            ) : (
              <>
                <FileText className="w-4 h-4 mr-2" />
                Générer l'appel à cotisation
              </>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default CallGenerator;
